import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";

const DesignVisual = () => (
  <div className="relative h-36 md:h-40">
    <motion.div
      initial={{ y: 24, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ margin: "-10%" }}
      transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="absolute inset-x-8 bottom-6 top-1 rounded-lg border border-[#D4AF37]/25 bg-[#121212]/70 backdrop-blur-md"
    />
    <motion.div
      initial={{ y: 34, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ margin: "-10%" }}
      transition={{ delay: 0.2, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      className="absolute inset-x-16 bottom-0 top-8 rounded-lg border border-[#D4AF37]/40 bg-gradient-to-br from-[#1A1510] to-[#0a0a0a]"
    >
      <span className="absolute right-3 top-3 h-2 w-2 animate-pulse rounded-full bg-[#FFD700]" />
    </motion.div>
  </div>
);

const DevVisual = () => (
  <div className="rounded-lg border border-white/10 bg-[#0a0a0a]/80 p-4 font-mono text-[11px] leading-6 text-[#BDBDB6] md:text-xs">
    {[
      'const vision = await kunde.erzaehlt();',
      'design.entwerfen({ mut: true, gold: true });',
      'deploy("sf-experience", { wow: true });',
    ].map((l, i) => (
      <motion.p
        key={l}
        initial={{ opacity: 0, x: -14 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ margin: "-10%" }}
        transition={{ delay: i * 0.3, duration: 0.6 }}
      >
        <span className="mr-3 text-[#D4AF37]">{String(i + 1).padStart(2, "0")}</span>
        {l}
      </motion.p>
    ))}
  </div>
);

const BrandVisual = () => (
  <div className="flex items-center gap-6">
    <div className="flex -space-x-4">
      {["#D4AF37", "#F5F5F0", "#1A1510"].map((c, i) => (
        <motion.span
          key={c}
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          viewport={{ margin: "-10%" }}
          transition={{ delay: i * 0.18, type: "spring", stiffness: 200, damping: 14 }}
          className="h-12 w-12 rounded-full border border-[#D4AF37]/30 md:h-14 md:w-14"
          style={{ backgroundColor: c }}
        />
      ))}
    </div>
    <span className="gold-text font-serif-accent text-6xl italic md:text-7xl">Aa</span>
  </div>
);

const PerfVisual = () => (
  <div>
    <div className="flex items-baseline gap-2">
      <span className="gold-text font-display text-5xl font-extrabold md:text-6xl">0.4</span>
      <span className="text-sm tracking-[0.3em] text-[#BDBDB6]">SEK. LADEZEIT</span>
    </div>
    <div className="mt-4 space-y-2">
      {[100, 72, 45].map((w, i) => (
        <motion.div
          key={w}
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ margin: "-10%" }}
          transition={{ delay: i * 0.15, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="h-1 origin-left rounded-full bg-gradient-to-r from-[#D4AF37] to-transparent"
          style={{ width: `${w}%` }}
        />
      ))}
    </div>
  </div>
);

const RespVisual = () => (
  <div className="flex items-end justify-center gap-3">
    {[
      { w: "w-40 md:w-48", h: "h-24 md:h-28", d: 0 },
      { w: "w-24 md:w-28", h: "h-20 md:h-24", d: 0.15 },
      { w: "w-10 md:w-12", h: "h-20 md:h-24", d: 0.3 },
    ].map((f, i) => (
      <motion.div
        key={i}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ margin: "-10%" }}
        transition={{ delay: f.d, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={`${f.w} ${f.h} rounded-md border border-[#D4AF37]/30 bg-gradient-to-b from-[#1A1510] to-[#0a0a0a]`}
      >
        <div className="mx-auto mt-2 h-1 w-1/2 rounded bg-[#D4AF37]/40" />
        <div className="mx-3 mt-2 space-y-1">
          <div className="h-0.5 rounded bg-white/15" />
          <div className="h-0.5 w-2/3 rounded bg-white/15" />
        </div>
      </motion.div>
    ))}
  </div>
);

const services = [
  {
    id: "01",
    title: "Webdesign",
    desc: "Individuelles Webdesign, das Ihre Marke sofort erkennbar macht. Maßgeschneidert für Ihren Betrieb statt Vorlage von der Stange — jedes Pixel mit Absicht gesetzt.",
    tags: ["UI/UX", "Art Direction", "Motion"],
    visual: <DesignVisual />,
  },
  {
    id: "02",
    title: "Entwicklung",
    desc: "Sauberer Code, starke Technik. Wir programmieren Ihre Website selbst — schnell, zuverlässig und wartbar, ob Neubau oder Relaunch einer bestehenden Seite.",
    tags: ["React", "CMS", "3D & WebGL"],
    visual: <DevVisual />,
  },
  {
    id: "03",
    title: "Branding",
    desc: "Farben, Formen und Typografie, die zusammen eine unverwechselbare Marke ergeben — konsistent vom Logo über die Website bis zur Visitenkarte.",
    tags: ["Logo", "Corporate Design", "Typografie"],
    visual: <BrandVisual />,
  },
  {
    id: "04",
    title: "Performance",
    desc: "Ladezeiten unter einer Sekunde. Optimiert für Core Web Vitals und Suchmaschinen, damit Ihre Kunden Sie bei Google finden — Geschwindigkeit als Designelement.",
    tags: ["Core Web Vitals", "SEO", "Speed"],
    visual: <PerfVisual />,
  },
  {
    id: "05",
    title: "Responsive Design",
    desc: "Überzeugend auf jedem Gerät — vom Smartphone bis zum großen Bildschirm. Die Mehrheit Ihrer Besucher kommt vom Handy, dort muss alles sitzen.",
    tags: ["Mobile First", "Tablet", "Desktop"],
    visual: <RespVisual />,
  },
];

export default function Services() {
  return (
    <section id="leistungen" data-testid="services-section" className="relative z-10 px-6 py-28 md:px-14 md:py-40">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mb-20"
        >
          <p className="mb-6 text-[11px] tracking-[0.45em] text-[#D4AF37] md:text-sm">LEISTUNGEN · WEBDESIGN UND ENTWICKLUNG</p>
          <h2 className="max-w-4xl font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tight sm:text-5xl lg:text-6xl">
            Webdesign, das mehr kann als gut{" "}
            <em className="gold-text font-serif-accent normal-case italic">aussehen.</em>
          </h2>
        </motion.div>

        <div>
          {services.map((s) => (
            <motion.div
              key={s.id}
              data-testid={`service-${s.id}`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="group grid items-center gap-8 border-t border-white/5 py-14 transition-colors duration-500 hover:border-[#D4AF37]/20 md:grid-cols-12 md:py-16"
            >
              <div className="md:col-span-2">
                <span className="text-stroke-gold font-display text-6xl font-extrabold transition-opacity duration-500 group-hover:opacity-100 md:text-7xl">
                  {s.id}
                </span>
              </div>
              <div className="md:col-span-6">
                <h3 className="flex items-center gap-3 font-display text-2xl font-bold uppercase tracking-tight transition-colors duration-500 group-hover:text-[#FFD700] md:text-3xl">
                  {s.title}
                  <ArrowUpRight
                    size={22}
                    className="text-[#D4AF37] opacity-0 transition-[opacity,transform] duration-500 group-hover:translate-x-1 group-hover:opacity-100"
                  />
                </h3>
                <p className="mt-4 max-w-lg text-base font-light leading-relaxed text-[#BDBDB6] md:text-lg">
                  {s.desc}
                </p>
                <div className="mt-5 flex flex-wrap gap-2">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-white/10 px-3 py-1 text-[11px] tracking-[0.2em] text-[#BDBDB6]"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="md:col-span-4">{s.visual}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
